// The public room list, and the inside-a-room seat grid. Renders straight into a container element with
// plain innerHTML/event listeners — same style as heroSelect.ts/hud.ts, no framework. Re-renders on
// every `roomState`/`roomList` push (the server sends `roomState` to everyone in the room, not just the
// actor, so a teammate's pick shows up here without this client having done anything).
//
// `applyLobbyMessage` is the actual state machine (seats/teams/bots/ready/leave/room-closing-under-you),
// deliberately pure and DOM-free — same split as heroSelect.ts's moveSelection/firstAvailable — so it is
// unit-testable without a `document` (this project's test setup has none; see test/client/net/lobbyView.
// test.ts). `renderLobby` below is the thin, unverified-without-a-browser DOM wrapper around it.
import { renderHeroSelect, type HeroSelectHandle } from '../heroSelect.js';
import { errorText } from './errorText.js';
import { roomInviteUrl, copyToClipboard } from './inviteLink.js';
import { escapeHtml, playerLabel } from '../nameStore.js';
import { t } from '../i18n/index.js';
import { heroName as heroNameOf, teamFullName } from '../i18n/names.js';
import { teamProfile } from '../teamProfile.js';
import { radarChartSvg, radarSummaryText } from '../radarChart.js';
import type { ClientMessage, RoomId, RoomState, RoomSummary, SeatIndex, SeatView, ServerMessage, Team } from '../../shared/protocol.js';
import type { HeroKind } from '../../core/heroes.js';

export type LobbyState = { room: RoomState | null; rooms: RoomSummary[]; statusText: string };
export const INITIAL_LOBBY_STATE: LobbyState = { room: null, rooms: [], statusText: '' };

/** Every transition the lobby screen can go through, from the server's side alone — matchStart is
    handled a level up (net/entry.ts), since it switches screens entirely rather than updating this one. */
export function applyLobbyMessage(state: LobbyState, msg: ServerMessage): LobbyState {
  switch (msg.type) {
    case 'roomList': return { ...state, rooms: msg.rooms };
    case 'roomState': return { ...state, room: msg.room };
    case 'roomClosed': return { ...state, room: null, statusText: t('lobby.closed') };
    case 'error': return { ...state, statusText: errorText(msg.code) };
    default: return state;
  }
}

export function otherHeroes(room: RoomState, exceptSeat: SeatIndex): Partial<Record<HeroKind, string>> {
  const taken: Partial<Record<HeroKind, string>> = {};
  for (const s of room.seats) if (s.index !== exceptSeat && s.hero) taken[s.hero] = t('lobby.heroTaken');
  return taken;
}

export type SeatActionKind = 'sit-or-bot' | 'unbot' | 'mine' | 'none';

/** What controls a seat row shows: an empty seat can be sat in or turned into a bot; a bot (once the
    room has at least one seated member — `viewerSeated`) can be removed; your own seat gets the hero
    picker and a team switch; anyone else's occupied seat gets nothing to click. */
export function seatActionKind(seat: SeatView, room: RoomState): SeatActionKind {
  if (seat.kind === 'empty') return 'sit-or-bot';
  if (seat.kind === 'bot') return room.yourSeat !== null ? 'unbot' : 'none';
  if (room.yourSeat === seat.index) return 'mine';
  return 'none';
}

export function isSeatReady(seat: SeatView): boolean {
  return seat.kind === 'bot' || (seat.kind === 'human' && seat.ready);
}

/** The name to show for a seat, or null for an empty/bot seat (their own badge already says what they
    are). `viewerName` overrides the wire value for the viewer's own seat: it reflects the name field the
    player is looking at right now, without waiting on a round trip to confirm it back. */
export function seatDisplayName(seat: SeatView, room: RoomState, viewerName: string): string | null {
  if (seat.kind === 'empty' || seat.kind === 'bot') return null;
  if (room.yourSeat === seat.index) return viewerName;
  return seat.name;
}

/** "Комната Deniska" — never the room's hex id (that was the whole point of this screen). */
export function roomTitle(room: RoomSummary): string {
  return t('lobby.room', { name: playerLabel(room.creatorName) });
}

/** The room screen's own title: a nameless creator comes back as '' (protocol 21) — for a room the viewer
    is actually IN, their own (locally known) name stands in rather than showing them "Комната Игрок" about their own
    room. Any other room in the public list has no such local truth to fall back on and keeps `roomTitle`
    as-is; this is scoped to `RoomState` (a room you hold a view of), not `RoomSummary`, on purpose. */
export function displayRoomTitle(room: RoomState, viewerName: string): string {
  if (room.yourSeat !== null && !room.creatorName) return t('lobby.room', { name: playerLabel(viewerName) });
  return roomTitle(room);
}

export function seatPortraitSrc(hero: HeroKind | null): string | null {
  return hero ? `portraits/${hero}.jpg` : null;
}

/* A free seat's portrait slot: an empty frame and a plain bust silhouette, stroked like heroIcons.ts's
   marks rather than left as a blank rectangle that reads as a missing image. */
const EMPTY_PORTRAIT_SVG = `<svg class="seat-portrait-empty" viewBox="0 0 48 64" aria-hidden="true">
  <rect x="2.5" y="2.5" width="43" height="59" fill="none" stroke="currentColor" stroke-width="1.1" stroke-dasharray="3 3"/>
  <circle cx="24" cy="23" r="8.5" fill="none" stroke="currentColor" stroke-width="1.3"/>
  <path d="M9 52c1.5-11 8.5-17 15-17s13.5 6 15 17" fill="none" stroke="currentColor" stroke-width="1.3"/>
</svg>`;

export type LobbyView = {
  handleMessage(msg: ServerMessage): void;
  destroy(): void;
};

/* `heroSelectEl` is a container OUTSIDE `root` (public/index.html's dedicated `#heroSelect`, the same
   element the offline picker uses) — deliberately not a child `root.appendChild`ed overlay. `render()`
   below replaces `root.innerHTML` wholesale on every `roomState`/`roomList` push (a teammate's pick has to
   show up without this client doing anything — see the module header), which would silently destroy any
   element mounted inside it the instant the picker's own `onConfirm` hadn't already closed it — exactly
   what happened before this split: the picker looked like a dead button because it was live for a moment,
   then wiped by the next push with no error to show for it. */
export function renderLobby(root: HTMLElement, heroSelectEl: HTMLElement, send: (msg: ClientMessage) => void, playerName: string, onExit?: () => void): LobbyView {
  let state = INITIAL_LOBBY_STATE;
  let destroyed = false;  // a copy result resolving after destroy() must not redraw the shared #lobby element
  let heroSelect: HeroSelectHandle | null = null;
  let openSeat: SeatIndex | null = null;
  // The copy button's own result (`renderRoom` rebuilds `root.innerHTML` wholesale on every `roomState` —
  // e.g. a teammate taking a seat mid-copy — which would otherwise silently reset the feedback/field back
  // to blank/hidden, and a copy that resolves AFTER such a rebuild would be writing into detached nodes
  // rather than the live ones). Kept here, in closure state, and read back by `renderRoom` on every render
  // instead of written straight to a captured DOM reference — same fix shape as `heroSelect`/`openSeat`
  // above. Scoped by room id so it resets automatically on a genuinely different room, never on a same-room
  // re-render (see `copyDisplayFor` below).
  let copyState: { roomId: RoomId; feedback: string; fieldValue: string | null } | null = null;
  function copyDisplayFor(roomId: RoomId): { feedback: string; fieldValue: string | null } {
    return copyState && copyState.roomId === roomId ? copyState : { feedback: '', fieldValue: null };
  }

  function closeHeroSelect() {
    heroSelect?.destroy();
    heroSelect = null;
    openSeat = null;
    heroSelectEl.hidden = true;
  }

  function renderRoomList(rooms: RoomSummary[]) {
    root.innerHTML = `
      <div class="lobby-plate"><h1>${t('lobby.rooms')}</h1></div>
      ${state.statusText ? `<div class="lobby-status">${state.statusText}</div>` : ''}
      <div class="lobby-actions">
        ${onExit ? `<button type="button" class="lobby-leave" id="lobbyExit">${t('common.toMenu')}</button>` : ''}
        <button type="button" class="lobby-create" id="lobbyCreate">${t('lobby.create')}</button>
      </div>
      <ul class="room-list">
        ${rooms.length ? rooms.map(r => {
          const taken = r.seats.filter(s => s.kind !== 'empty').length;
          return `<li class="room-card">
            <span class="room-card-title">${escapeHtml(roomTitle(r))}</span>
            <span class="room-card-seats">${t('lobby.seats', { n: taken })}${disconnectedHint(r)}</span>
            <button type="button" class="room-card-join" data-join="${r.id}">${t('lobby.join')}</button>
          </li>`;
        }).join('') : `<li class="room-empty">
            <span class="room-empty-title">${t('lobby.emptyTitle')}</span>
            <span class="room-empty-sub">${t('lobby.emptySub')}</span>
          </li>`}
      </ul>`;
    root.querySelector('#lobbyCreate')!.addEventListener('click', () => send({ type: 'createRoom' }));
    if (onExit) root.querySelector('#lobbyExit')!.addEventListener('click', exit);
    root.querySelectorAll<HTMLButtonElement>('[data-join]').forEach(btn => {
      btn.addEventListener('click', () => send({ type: 'joinRoom', roomId: btn.dataset.join! }));
    });
  }

  function disconnectedHint(_r: RoomSummary): string { return ''; }  // room for a future "кто-то отключился" hint, not needed yet

  function openHeroSelect(room: RoomState, seat: SeatIndex) {
    closeHeroSelect();
    openSeat = seat;
    heroSelectEl.hidden = false;
    heroSelect = renderHeroSelect(heroSelectEl, {
      taken: otherHeroes(room, seat),
      initial: room.seats[seat]!.hero ?? undefined,
      confirmLabel: t('lobby.ready'),
      onBack: closeHeroSelect,
      onConfirm(hero) {
        send({ type: 'pickHero', hero });
        send({ type: 'ready', ready: true });
        closeHeroSelect();
      },
    });
  }

  function seatHtml(s: SeatView, room: RoomState): string {
    const mine = room.yourSeat === s.index;
    const ready = isSeatReady(s);
    const kind = seatActionKind(s, room);
    const disconnected = s.kind === 'human' && !s.connected;
    const portrait = seatPortraitSrc(s.hero);
    const heroName = s.hero ? heroNameOf(s.hero) : null;
    const heading = s.kind === 'empty' ? t('lobby.free') : s.kind === 'bot' ? t('res.bot')
      : escapeHtml(playerLabel(seatDisplayName(s, room, playerName)));

    // Switching sides is now just sitting in a free seat on the other team — `data-seat` already sends
    // pickSeat regardless of whether the viewer holds a different seat already; the server treats that as
    // a move (vacate + take) once it lands. No separate team control needed or offered.
    const actions =
      kind === 'sit-or-bot' ? `<button type="button" class="seat-act" data-seat="${s.index}">${t('lobby.sit')}</button>
        <button type="button" class="seat-act seat-act-quiet" data-bot="${s.index}">${t('res.bot')}</button>` :
      kind === 'unbot' ? `<button type="button" class="seat-act" data-unbot="${s.index}">${t('lobby.unbot')}</button>` :
      kind === 'mine' ? `<button type="button" class="seat-act" data-hero="${s.index}">${s.hero ? t('lobby.changeHero') : t('lobby.pickHero')}</button>` : '';

    // The hero's own art is the card's background (bled, not a framed thumbnail), zoomed and positioned
    // (see .seat-card CSS) so the figure stands in the card's right side. The wash runs left-to-right
    // instead of bottom-up: strong behind the text column on the left, clear by the middle, so it never
    // sits over the face — a vertical wash was hiding exactly the head it was meant to sit beside.
    const bg = portrait
      ? ` style="background-image: linear-gradient(to right, rgba(239,227,198,.94) 0%, rgba(239,227,198,.88) 22%, rgba(239,227,198,.20) 40%, rgba(239,227,198,0) 55%), url('${portrait}')"`
      : '';

    return `<li class="seat-card ${s.kind}${mine ? ' mine' : ''}${ready ? ' ready' : ''}"${bg}>
      ${portrait ? '' : EMPTY_PORTRAIT_SVG}
      <div class="seat-overlay">
        <span class="seat-name">${heading}${disconnected ? t('lobby.disconnected') : ''}</span>
        ${heroName ? `<span class="seat-hero">${heroName}</span>` : ''}
        ${ready ? `<span class="seat-ready">${t('lobby.ready')}</span>` : ''}
        <div class="seat-actions">${actions}</div>
      </div>
    </li>`;
  }

  function renderRoom(r: RoomState) {
    const byTeam = (team: Team) => r.seats.filter(s => s.team === team);
    const pickedHeroes = (team: Team) => byTeam(team).map(s => s.hero).filter((h): h is HeroKind => h !== null);
    const profileA = teamProfile(pickedHeroes('a')), profileB = teamProfile(pickedHeroes('b'));
    const copy = copyDisplayFor(r.id);
    root.innerHTML = `
      <div class="lobby-plate"><h1>${escapeHtml(displayRoomTitle(r, playerName))}</h1></div>
      ${state.statusText ? `<div class="lobby-status">${state.statusText}</div>` : ''}
      <div class="lobby-invite">
        <button type="button" class="lobby-invite-btn" id="lobbyCopyLink">${t('lobby.copyLink')}</button>
        <span class="lobby-invite-feedback" id="lobbyCopyFeedback" role="status" aria-live="polite">${escapeHtml(copy.feedback)}</span>
        <input type="text" class="lobby-invite-field" id="lobbyLinkField" readonly${copy.fieldValue === null ? ' hidden' : ''}
               aria-label="${t('lobby.linkAria')}" value="${escapeHtml(copy.fieldValue ?? '')}">
      </div>
      <div class="lobby-teams">
        <div class="lobby-team a"><h2>${teamFullName('a')}</h2><ul>${byTeam('a').map(s => seatHtml(s, r)).join('')}</ul></div>
        <div class="lobby-team b"><h2>${teamFullName('b')}</h2><ul>${byTeam('b').map(s => seatHtml(s, r)).join('')}</ul></div>
      </div>
      <div class="lobby-radars">
        <div class="radar-card a">${radarChartSvg(profileA, 'var(--red)')}<p class="radar-caption">${escapeHtml(radarSummaryText(profileA))}</p></div>
        <div class="radar-card b">${radarChartSvg(profileB, 'var(--blue)')}<p class="radar-caption">${escapeHtml(radarSummaryText(profileB))}</p></div>
      </div>
      <button type="button" class="lobby-leave" id="lobbyLeave">${t('lobby.leave')}</button>`;
    root.querySelector('#lobbyLeave')!.addEventListener('click', () => {
      closeHeroSelect();
      send({ type: 'leaveRoom' });
      // The server never replies to the leaver directly (room.ts's leaveRoom only `sync()`s the remaining
      // members) — without this the room screen just sits there forever, since nothing else ever tells
      // this client it left. Same local transition the `roomClosed` branch already does.
      state = { ...state, room: null };
      send({ type: 'listRooms' });
      render();
    });
    root.querySelectorAll<HTMLButtonElement>('[data-seat]').forEach(b => b.addEventListener('click', () => send({ type: 'pickSeat', seat: Number(b.dataset.seat) as SeatIndex })));
    root.querySelectorAll<HTMLButtonElement>('[data-bot]').forEach(b => b.addEventListener('click', () => send({ type: 'setBot', seat: Number(b.dataset.bot) as SeatIndex, bot: true })));
    root.querySelectorAll<HTMLButtonElement>('[data-unbot]').forEach(b => b.addEventListener('click', () => send({ type: 'setBot', seat: Number(b.dataset.unbot) as SeatIndex, bot: false })));
    root.querySelectorAll<HTMLButtonElement>('[data-hero]').forEach(b => b.addEventListener('click', () => openHeroSelect(r, Number(b.dataset.hero) as SeatIndex)));

    root.querySelector('#lobbyCopyLink')!.addEventListener('click', () => {
      const url = roomInviteUrl(r.id);
      copyToClipboard(url).then(ok => {
        // The copy started in THIS room, but resolves later (copyToClipboard.ts's own clipboard timeout,
        // or a slow permission prompt) — if the viewer has since left or the room changed, its result is
        // stale and belongs nowhere: applying it would touch a room the player is no longer looking at,
        // same class of bug as writing into nodes a wholesale re-render already detached.
        if (destroyed || state.room?.id !== r.id) return;
        copyState = ok
          ? { roomId: r.id, feedback: t('common.linkCopied'), fieldValue: null }
          // Neither the Clipboard API nor execCommand worked — the last fallback is showing the link in a
          // plain readonly field the player can select and copy by hand (Ctrl+C).
          : { roomId: r.id, feedback: '', fieldValue: url };
        render();
        if (!ok) {
          // Pre-selecting is a convenience, not the point of this branch, so a host missing select()
          // entirely still leaves a visible, working field rather than throwing out of the whole handler.
          const field = root.querySelector('#lobbyLinkField') as HTMLInputElement | null;
          try { field?.focus(); field?.select(); } catch { /* the field is still visible either way */ }
        }
      });
    });
  }

  function render() {
    // The picker survives a room re-render as long as it's still showing for the viewer's own current
    // seat — otherwise (left the room, room closed under you, seat no longer yours) it's stale, close it.
    if (openSeat !== null && state.room?.yourSeat === openSeat) heroSelect?.setTaken(otherHeroes(state.room, openSeat));
    else if (openSeat !== null) closeHeroSelect();
    if (state.room) renderRoom(state.room); else renderRoomList(state.rooms);
    state = { ...state, statusText: '' };  // one-shot: shown for exactly the render it was set for
  }

  // Esc leaves the room list for the menu; inside a room it only closes the picker (heroSelect.ts's own Esc).
  // Not before the server's first word: coming back from a match, the list shown until then is a
  // placeholder, and an Esc still held from the match must not throw the player out of what follows.
  let settled = false;
  const exit = () => { if (settled && onExit) onExit(); };
  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && !e.defaultPrevented && state.room === null && openSeat === null) exit();
  }
  window.addEventListener('keydown', onKeydown);

  return {
    handleMessage(msg) {
      if (msg.type === 'roomList' || msg.type === 'roomState' || msg.type === 'roomClosed') settled = true;
      const wasInRoom = state.room !== null;
      state = applyLobbyMessage(state, msg);
      if (msg.type === 'roomClosed') send({ type: 'listRooms' });
      if (msg.type === 'roomList' && wasInRoom) return;  // a background list refresh while inside a room: nothing to redraw
      render();
    },
    destroy() { destroyed = true; window.removeEventListener('keydown', onKeydown); closeHeroSelect(); root.innerHTML = ''; },
  };
}
